
import Vue from "vue";
import { CSSProperties } from "vue/types/jsx";
import { Component, Watch } from "vue-property-decorator";
import BuilderElement from "./BuilderElement.vue";
import NodeChrome from "./OverlaidUI/NodeChrome.vue";
import CanvasChrome from "./OverlaidUI/CanvasChrome.vue";
import {
  uiStore,
  documentStore,
  globalStore,
  configStore
} from "@/store/store";
import EmptyContainer from "@/components/Canvas/Empty/EmptyContainer.vue";
import {
  handleSelectorsChanged,
  handleGlobalSettingsChanged,
  handlePresetsChanged,
  handleVariablesChanged
} from "@/lib/canvas/reacting-to-global-store-changes";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import { handleOxySelectorsChanged } from "@/oxygen/classes/lib/builder";
import { updateSelectorsFromStorageEvent } from "@/oxygen/classes/lib/classes";
import InlineEditorBackdrop from "./InlineEditor/InlineEditorBackdrop.vue";
import DragGhostElement from "@/components/Add/DragGhostElement.vue";

@Component({
  components: {
    DragGhostElement,
    EmptyContainer,
    BuilderElement,
    NodeChrome,
    CanvasChrome
  }
})
export default class Canvas extends Vue {
  render() {
    const children = () => {
      if (this.rootElement.children.length === 0) {
        return <EmptyContainer />;
      }

      return this.rootElement.children.map(element => (
        <BuilderElement
          // @ts-ignore
          key={element.id}
          // @ts-ignore
          parentId={this.rootElement.id}
          element={element}
        />
      ));
    };

    return (
      <div
        key={this.canvasKey}
        class={this.classList}
        id="breakdance_canvas"
        style={this.style}
      >
        {children()}
        {!this.isPreviewMode && <NodeChrome />}
        {(!this.isPreviewMode && !this.isOnboardingMode) ?? <CanvasChrome />}
        {!this.isPreviewMode && <CanvasChrome />}
        {!this.isPreviewMode && <InlineEditorBackdrop />}
        {!this.isPreviewMode && <DragGhostElement />}
        {this.previewSettings.backgroundColor && this.previewGlobalStyle()}
      </div>
    );
  }

  mounted() {
    uiStore.setCanvasState("normal");

    addEventListener("storage", this.onStorage);
  }

  beforeDestroy() {
    removeEventListener("storage", this.onStorage);
  }

  onStorage(event: StorageEvent) {
    if (event.key === "oxy_selectors") {
      updateSelectorsFromStorageEvent(event);
    }
  }

  get canvasKey() {
    return uiStore.canvasKey;
  }

  get builderMode() {
    return configStore.builderMode || "breakdance";
  }

  get isPreviewMode() {
    return uiStore.isPreviewMode;
  }

  get isOnboardingMode() {
    return window.Breakdance.isOnboarding;
  }

  get classList() {
    return {
      [this.builderMode]: true,
      canvas: true,
      dragging:
        uiStore.isCanvasElementBeingDragged || uiStore.isAddElementBeingDragged
    };
  }

  get rootElement() {
    return documentStore.document.tree.root;
  }

  get globalProperties() {
    return globalStore.globalProperties;
  }

  get selectors() {
    return globalStore.cssSelectors;
  }

  get presets() {
    return globalStore.presets;
  }

  get oxySelectors() {
    return globalStore.oxySelectors;
  }

  get variables() {
    return globalStore.variables;
  }

  get previewSettings() {
    const { preview } = documentStore.document.documentMeta.blockSettings || {
      preview: {}
    };

    return {
      width: preview.width,
      height: preview.height,
      backgroundColor: preview.backgroundColor,
      isCentered: preview.isCentered
    };
  }

  get style(): CSSProperties {
    const { height, width, isCentered } = this.previewSettings;

    const centerStyles: CSSProperties = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    };

    return {
      maxWidth: width ? `100%` : undefined,
      width: width ? `${width}px` : undefined,
      height: height ? `${height}px` : undefined,
      margin: width || height ? `0 auto` : undefined,
      ...(isCentered ? centerStyles : {})
    };
  }

  previewGlobalStyle() {
    const { backgroundColor } = this.previewSettings;
    const bgProperty =
      this.builderMode === "oxygen"
        ? "background-color"
        : "--bde-background-color";

    return (
      <style>
        {`
          :root,
         body {
          ${bgProperty}: ${backgroundColor};
         }
        `}
      </style>
    );
  }

  @Watch("globalProperties", { deep: true, immediate: true })
  onGlobalSettingsChanged() {
    handleGlobalSettingsChanged(getBuilderCanvasDocument());
  }

  @Watch("selectors", { deep: true, immediate: true })
  onSelectorsChanged() {
    handleSelectorsChanged(getBuilderCanvasDocument());
  }

  @Watch("presets", { deep: true, immediate: true })
  onPresetsChanged() {
    handlePresetsChanged(getBuilderCanvasDocument());
  }

  @Watch("oxySelectors", { deep: true, immediate: true })
  onOxySelectorsChanged() {
    handleOxySelectorsChanged(getBuilderCanvasDocument());
  }

  @Watch("variables", { deep: true, immediate: true })
  onVariablesChanged() {
    handleVariablesChanged(getBuilderCanvasDocument());
    handleOxySelectorsChanged(getBuilderCanvasDocument());
  }
}
